import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { ModalidadeBadge } from './ModalidadeBadge';
import {
  LayoutDashboard,
  Calendar,
  Users,
  UserCircle,
  FileText,
  DollarSign,
  Settings,
  LogOut,
  ClipboardList,
  Stethoscope,
  History,
  BarChart3,
  Building2,
  ChevronLeft,
  UserCog,
  Menu,
  X,
  MessageSquare,
  FileSignature,
  Shield,
  ShieldCheck,
  FilePlus,
  BookOpen,
  FileEdit,
  Clock,
  Coffee,
  GraduationCap,
  FolderLock,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { useState, useEffect } from 'react';
import NotificationBell from './NotificationBell';
import BloqueadosBell from './BloqueadosBell';
import { useGovernancaStatus } from '../hooks/useGovernancaStatus';

// Brand Colors from Catavento Logo
const BRAND_COLORS = {
  coral: { bg: '#E8A598', text: '#7A3527', light: '#E8A59830' },      // Paciente
  blue: { bg: '#7EB3D8', text: '#1E4C6E', light: '#7EB3D830' },       // Recepção
  yellow: { bg: '#F0E68C', text: '#7A7015', light: '#F0E68C30' },     // Admin
  green: { bg: '#7FBF7F', text: '#266126', light: '#7FBF7F30' },      // Profissional
  pearl: { bg: '#E2E4E9', text: '#52525B', light: '#E2E4E920' },      // Neutro
};

// URL da logomarca oficial
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_clinical-scheduler/artifacts/dh1ga4hw_LOGO_interequipe-removebg_sem%20nome.png';

const Sidebar = () => {
  const { user, signOut, authFetch, profissionalAtivoId, setProfissionalAtivoId } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [reposicoesPendentes, setReposicoesPendentes] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Governança: acinzenta itens da sidebar quando prof está bloqueado (a partir de 20/08)
  const governanca = useGovernancaStatus();
  const estaBloqueado = user?.perfil === 'profissional' && governanca.esta_bloqueado === true;
  
  // Múltiplas especialidades do profissional
  const profissionais = user?.profissionais || (user?.profissional ? [user.profissional] : []);
  const hasMultiplosProfissionais = user?.perfil === 'profissional' && profissionais.length > 1;
  const profissionalAtivo = profissionais.find(p => p.id === profissionalAtivoId) || profissionais[0];

  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Polling de solicitações de reposição pendentes (apenas Admin)
  useEffect(() => {
    if (user?.perfil !== 'administrador') return;

    const fetchPendentes = async () => {
      // 2026-08 Perf: nao poll quando aba nao esta visivel
      if (document.hidden) return;
      try {
        const res = await authFetch('/reposicoes/pendentes/count', {
          suppressAuthRedirect: true,
          timeout: 8000
        });
        setReposicoesPendentes(res.data?.count || 0);
      } catch (err) {
        // Silencioso; badge some se falhar
      }
    };

    // 2026-08 Perf: intervalo aumentado de 30s -> 90s (badge nao precisa ser em tempo real)
    // + refetch imediato quando aba volta ao foco (usuaria percebe atualizacao)
    const timeout = setTimeout(fetchPendentes, 1000);
    const interval = setInterval(fetchPendentes, 90000);
    const onVisChange = () => { if (!document.hidden) fetchPendentes(); };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.perfil]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  // Get role color scheme
  const getRoleColors = (perfil) => {
    switch (perfil) {
      case 'administrador': return BRAND_COLORS.yellow;
      case 'recepcao': return BRAND_COLORS.blue;
      case 'profissional': return BRAND_COLORS.green;
      case 'paciente': return BRAND_COLORS.coral;
      default: return BRAND_COLORS.pearl;
    }
  };

  const roleColors = getRoleColors(user?.perfil);

  // Definir menus baseados no perfil
  const getMenuItems = () => {
    const role = user?.perfil || 'paciente';

    const adminMenu = [
      { label: 'Inteligência', icon: BarChart3, path: '/dashboard/relatorios' },
      { label: 'Agendamentos', icon: Calendar, path: '/dashboard/agendamentos' },
      { label: 'Pacientes', icon: Users, path: '/dashboard/pacientes' },
      { label: 'Profissionais', icon: Stethoscope, path: '/dashboard/profissionais' },
      { label: 'Usuários', icon: UserCog, path: '/dashboard/usuarios' },
      { label: 'Prontuários', icon: FileText, path: '/dashboard/prontuarios' },
      { label: 'Salas', icon: Building2, path: '/dashboard/salas' },
      { label: 'Chat', icon: MessageSquare, path: '/dashboard/mural' },
      { label: 'Convênios', icon: ClipboardList, path: '/dashboard/convenios' },
      { label: 'Documentos', icon: FilePlus, path: '/dashboard/documentos-clinica' },
      { label: 'Modelos Docs', icon: FileEdit, path: '/dashboard/templates-documentos' },
      { label: 'Docs Admin', icon: FolderLock, path: '/dashboard/docs-admin' },
      { label: 'Normas', icon: BookOpen, path: '/dashboard/documentacao-interna' },
      { label: 'Tutoriais', icon: GraduationCap, path: '/dashboard/tutoriais' },
      { label: 'Contratos', icon: FileSignature, path: '/dashboard/contratos' },
      { label: 'Financeiro', icon: DollarSign, path: '/dashboard/financeiro' },
      { label: 'Horários', icon: Clock, path: '/dashboard/config-horarios' },
      { label: 'Intervalos', icon: Coffee, path: '/dashboard/config-agenda' },
      { label: 'Auditoria', icon: Shield, path: '/dashboard/auditoria' },
      { label: 'Governança', icon: ShieldCheck, path: '/dashboard/governanca' },
      { label: 'Relatório Paciente', icon: FileText, path: '/dashboard/relatorios/paciente' },
      { label: 'Restaurar', icon: RotateCcw, path: '/dashboard/restaurar-desligamento' },
      { label: 'Reposições', icon: RotateCcw, path: '/dashboard/reposicoes', badge: reposicoesPendentes },
      { label: 'Configurações', icon: Settings, path: '/dashboard/configuracoes' },
    ];

    const recepcaoMenu = [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Agendamentos', icon: Calendar, path: '/dashboard/agendamentos' },
      { label: 'Pacientes', icon: Users, path: '/dashboard/pacientes' },
      { label: 'Salas', icon: Building2, path: '/dashboard/salas' },
      { label: 'Chat', icon: MessageSquare, path: '/dashboard/mural' },
      { label: 'Convênios', icon: ClipboardList, path: '/dashboard/convenios' },
      { label: 'Gerar Docs', icon: FilePlus, path: '/dashboard/gerar-documentos' },
      { label: 'Docs Gerados', icon: FileText, path: '/dashboard/documentos-clinica' },
      { label: 'Normas', icon: BookOpen, path: '/dashboard/documentacao-interna' },
      { label: 'Tutoriais', icon: GraduationCap, path: '/dashboard/tutoriais' },
      { label: 'Intervalos', icon: Coffee, path: '/dashboard/config-agenda' },
      { label: 'Configurações', icon: Settings, path: '/dashboard/configuracoes' },
    ];

    const profissionalMenu = [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Inteligência', icon: BarChart3, path: '/dashboard/relatorios' },
      { label: 'Minha Agenda', icon: Calendar, path: '/dashboard/minha-agenda' },
      { label: 'Minhas Pendências', icon: AlertTriangle, path: '/dashboard/minhas-pendencias' },
      { label: 'Reposições', icon: RotateCcw, path: '/dashboard/reposicoes' },
      { label: 'Prontuários', icon: FileText, path: '/dashboard/prontuarios' },
      { label: 'Relatório Paciente', icon: FileText, path: '/dashboard/relatorios/paciente' },
      { label: 'Documentos', icon: FilePlus, path: '/dashboard/documentos' },
      { label: 'Chat', icon: MessageSquare, path: '/dashboard/mural' },
      { label: 'Tutoriais', icon: GraduationCap, path: '/dashboard/tutoriais' },
      { label: 'Meu Contrato', icon: FileSignature, path: '/dashboard/contratos' },
      { label: 'Intervalos', icon: Coffee, path: '/dashboard/config-agenda' },
      { label: 'Configurações', icon: Settings, path: '/dashboard/configuracoes' },
    ];

    const pacienteMenu = [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Meus Agendamentos', icon: Calendar, path: '/dashboard/meus-agendamentos' },
      { label: 'Meu Histórico', icon: History, path: '/dashboard/historico' },
      { label: 'Meu Perfil', icon: UserCircle, path: '/dashboard/perfil' },
    ];

    switch (role) {
      case 'administrador':
        return adminMenu;
      case 'recepcao':
        return recepcaoMenu;
      case 'profissional':
        return profissionalMenu;
      case 'paciente':
      default:
        return pacienteMenu;
    }
  };

  const menuItems = getMenuItems();

  const getPerfilLabel = (perfil) => {
    const labels = {
      administrador: 'Administrador',
      recepcao: 'Recepção',
      profissional: 'Profissional',
      paciente: 'Paciente'
    };
    return labels[perfil] || 'Usuário';
  };

  // Pinwheel Logo Component - Using the OFFICIAL logo image
  const OfficialLogo = ({ size = 40 }) => (
    <img 
      src={LOGO_URL}
      alt="InterEquipe"
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );

  return (
    <>
      {/* Mobile Header Bar */}
      {isMobile && (
        <header 
          className="fixed top-0 left-0 right-0 h-16 flex items-center justify-between px-4 z-40"
          style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #E2E4E9' }}
        >
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            <OfficialLogo size={42} />
            <span 
              className="font-semibold"
              style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}
            >
              InterEquipe
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(user?.perfil === 'administrador' || user?.perfil === 'recepcao') && (
              <BloqueadosBell />
            )}
            {(user?.perfil === 'administrador' || user?.perfil === 'recepcao' || user?.perfil === 'profissional') && (
              <NotificationBell />
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-full transition-colors"
              style={{ color: '#52525B', backgroundColor: isOpen ? '#F4F4F5' : 'transparent' }}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </header>
      )}

      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen flex flex-col z-50 transition-transform duration-300
          ${isMobile ? 'w-72' : 'w-72'}
          ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}
          ${isMobile ? 'pt-0' : ''}
        `}
        style={{ 
          backgroundColor: '#FAFAFA', 
          borderRight: '1px solid #E2E4E9' 
        }}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/dashboard')}
          >
            <OfficialLogo size={48} />
            <div>
              <h1 
                className="text-lg font-semibold tracking-tight"
                style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}
              >
                InterEquipe
              </h1>
              <p 
                className="text-xs"
                style={{ color: '#A1A1AA' }}
              >
                Gestão Clínica
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isMobile && (user?.perfil === 'administrador' || user?.perfil === 'recepcao') && (
              <BloqueadosBell />
            )}
            {!isMobile && (user?.perfil === 'administrador' || user?.perfil === 'recepcao' || user?.perfil === 'profissional') && (
              <NotificationBell />
            )}
          </div>
        </div>

        <div className="px-4">
          <Separator style={{ backgroundColor: '#E2E4E9' }} />
        </div>

        {/* User Info - Role-colored */}
        <div className="p-4">
          <div 
            className="flex items-center gap-3 p-3 rounded-2xl transition-all"
            style={{ backgroundColor: roleColors.light }}
          >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
              style={{ backgroundColor: roleColors.bg }}
            >
              <span className="font-semibold" style={{ color: roleColors.text }}>
                {user?.nome?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="overflow-hidden">
              <p 
                className="font-medium text-sm truncate"
                style={{ color: '#18181B' }}
              >
                {user?.nome || user?.email?.split('@')[0] || 'Usuário'}
              </p>
              <p 
                className="text-xs font-medium truncate"
                style={{ color: roleColors.text }}
              >
                {getPerfilLabel(user?.perfil)}
                {user?.perfil === 'profissional' && profissionalAtivo?.especialidade && !hasMultiplosProfissionais && (
                  <>
                    {' · '}
                    {profissionalAtivo.especialidade}
                    {profissionalAtivo.modalidade_agenda && (
                      <>
                        {' '}
                        <ModalidadeBadge modalidade={profissionalAtivo.modalidade_agenda} size="xs" />
                      </>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Seletor de "chapéu" (profissional com >1 especialidade) */}
        {hasMultiplosProfissionais && (
          <div className="px-4 pb-2" data-testid="seletor-profissional-container">
            <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#71717A' }}>
              Atendendo como
            </label>
            <select
              data-testid="seletor-profissional"
              value={profissionalAtivoId || ''}
              onChange={(e) => {
                setProfissionalAtivoId(e.target.value);
                // Recarregar para atualizar todas as views com o novo escopo
                setTimeout(() => window.location.reload(), 100);
              }}
              className="w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2"
              style={{
                borderColor: roleColors.bg,
                backgroundColor: roleColors.light,
                color: roleColors.text,
              }}
            >
              {profissionais.map(p => {
                const modSuffix = p.modalidade_agenda === 'locacao'
                  ? ' · LOC'
                  : p.modalidade_agenda === 'percentual'
                    ? ' · %'
                    : '';
                return (
                  <option key={p.id} value={p.id}>
                    {p.especialidade}{modSuffix}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="px-4">
          <Separator style={{ backgroundColor: '#E2E4E9' }} />
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {menuItems.map((item) => {
              // Quando bloqueado, todos os itens ficam acinzentados EXCETO "Minhas Pendências"
              // (caminho livre para o Prof regularizar).
              const isAllowed = !estaBloqueado || item.path === '/dashboard/minhas-pendencias';
              return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={(e) => {
                  if (!isAllowed) {
                    e.preventDefault();
                    navigate('/dashboard/minhas-pendencias');
                    return;
                  }
                  handleNavClick(e);
                }}
                data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200
                  ${isActive
                    ? 'shadow-sm'
                    : (isAllowed ? 'hover:bg-white/80' : '')
                  }
                `}
                style={({ isActive }) => ({
                  backgroundColor: isActive ? 'white' : 'transparent',
                  color: isActive ? roleColors.text : (isAllowed ? '#52525B' : '#A3A3A3'),
                  borderLeft: isActive ? `3px solid ${roleColors.bg}` : '3px solid transparent',
                  opacity: isAllowed ? 1 : 0.55,
                  cursor: isAllowed ? 'pointer' : 'not-allowed',
                })}
                end={item.path === '/dashboard'}
                title={!isAllowed
                  ? 'Regularize suas pendências para liberar esta área'
                  : undefined}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium text-sm flex-1">
                  {item.label}
                </span>
                {item.badge > 0 && (
                  <span
                    data-testid={`sidebar-badge-${item.path.split('/').pop()}`}
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center animate-pulse shadow-sm"
                    title={`${item.badge} pendente(s)`}
                  >
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 mt-auto">
          <Separator style={{ backgroundColor: '#E2E4E9' }} className="mb-4" />
          <Button
            data-testid="logout-button"
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 h-11 rounded-xl transition-all hover:bg-red-50"
            style={{ color: '#DC2626' }}
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Sair</span>
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
